import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useMemo, useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Plus, CalendarDays, CalendarRange, List } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { useUser } from '@/shared/hooks/useAuth';
import { useMeetings, useMeetingMutations, useMeetingCalendar } from '../hooks/useMeetings';
import { MeetingsFilterBar } from '../components/MeetingsFilterBar';
import { MeetingCard } from '../components/MeetingCard';
import { MeetingFormModal } from '../components/MeetingFormModal';
import { MeetingDrawer } from '../components/MeetingDrawer';
import { MeetingCalendar } from '../components/MeetingCalendar';
import { ScheduleMeetingModal } from '../components/ScheduleMeetingModal';
import type { Meeting, CreateMeetingPayload, UpdateMeetingPayload, ScheduleMeetingPayload } from '../types';
import { localToday } from '@/shared/utils/utils';

type MeetingView = 'calendar' | 'list';

/** ISO date for the 42-cell month grid bounds. */
function gridBounds(month: Date): { from: string; to: string } {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const end = new Date(start);
  end.setDate(start.getDate() + 41);
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: iso(start), to: iso(end) };
}

export function MeetingsPage() {
  const user = useUser();
  const role = user?.role ?? '';
  const isPrivileged = ['admin', 'superadmin'].includes(role);

  // Authorization mirrors the backend: superadmin = any; admin = dept-scoped
  // (the list is already filtered to their dept); else organizer only.
  const canModify = useCallback(
    (meeting: Meeting) => isPrivileged || meeting.organizer_id === user?.id,
    [isPrivileged, user?.id],
  );

  // ─── Filters ────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const filters = useMemo(() => (status ? { status } : {}), [status]);

  // ─── Data ─────────────────────────────────────────────────────────────────
  const { data: meetings = [], isLoading, isError } = useMeetings(filters);
  const { createMeeting, updateMeeting, deleteMeeting, scheduleMeeting } = useMeetingMutations();

  // ─── UI state ───────────────────────────────────────────────────────────────
  const [view, setView] = useState<MeetingView>('calendar');
  const [month, setMonth] = useState<Date>(() => new Date());
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<string>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editMeeting, setEditMeeting] = useState<Meeting | null>(null);
  const [drawerMeeting, setDrawerMeeting] = useState<Meeting | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Meeting | null>(null);

  // A meeting_invite notification links here with ?meetingId= — before this,
  // every notification type except leads/deals dropped the user on the list
  // page with no way to jump straight to the specific record. Consumed once
  // the meeting is found, then stripped from the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const meetingId = searchParams.get('meetingId');
    if (!meetingId || meetings.length === 0) return;
    const found = meetings.find((m) => m.id === meetingId);
    if (found) {
      setDrawerMeeting(found);
      const next = new URLSearchParams(searchParams);
      next.delete('meetingId');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, meetings, setSearchParams]);

  // Calendar data for the visible month grid (+ CEO free/busy).
  const { from, to } = useMemo(() => gridBounds(month), [month]);
  const { data: calendar } = useMeetingCalendar(from, to);

  const handleSelectDate = (isoDate: string) => {
    setScheduleDate(isoDate);
    setScheduleOpen(true);
  };

  const handleSchedule = (payload: ScheduleMeetingPayload) => {
    scheduleMeeting.mutate(payload, {
      onSuccess: () => {
        toast.success('Meeting scheduled.');
        setScheduleOpen(false);
      },
      onError: () => toast.error('Failed to schedule meeting.'),
    });
  };

  // ─── Client-side search on title ─────────────────────────────────────────────
  const visibleMeetings = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return meetings;
    return meetings.filter((m) => (m.title ?? '').toLowerCase().includes(q));
  }, [meetings, search]);

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const todayIso = () => localToday();
  const handleCreate = () => {
    setScheduleDate(todayIso());
    setScheduleOpen(true);
  };

  const handleEdit = useCallback((meeting: Meeting) => {
    setEditMeeting(meeting);
    setFormOpen(true);
  }, []);

  const handleCreateSubmit = (payload: CreateMeetingPayload) => {
    createMeeting.mutate(payload, {
      onSuccess: () => {
        toast.success('Meeting created.');
        setFormOpen(false);
      },
      onError: () => toast.error('Failed to create meeting.'),
    });
  };

  const handleUpdateSubmit = (id: string, payload: UpdateMeetingPayload) => {
    updateMeeting.mutate(
      { id, payload },
      {
        onSuccess: () => {
          toast.success('Meeting updated.');
          setFormOpen(false);
          setEditMeeting(null);
          setDrawerMeeting(null);
        },
        onError: () => toast.error('Failed to update meeting.'),
      },
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMeeting.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Meeting deleted.');
        setDeleteTarget(null);
      },
      onError: () => toast.error('Failed to delete meeting.'),
    });
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full flex-col gap-4 pb-16">
      {/* Header */}
      <PageHeader
        icon={CalendarDays}
        eyebrow="Workspace"
        title="Meetings"
        description={<>{visibleMeetings.length} meeting{visibleMeetings.length !== 1 ? 's' : ''}</>}
        actions={
          <div className="flex items-center gap-2">
                  {/* View toggle */}
                  <div className="flex items-center gap-1 rounded border border-outline p-0.5">
                    <Button variant={view === 'calendar' ? 'secondary' : 'ghost'} size="sm" onClick={() => setView('calendar')}>
                      <CalendarRange className="h-4 w-4" /> Calendar
                    </Button>
                    <Button variant={view === 'list' ? 'secondary' : 'ghost'} size="sm" onClick={() => setView('list')}>
                      <List className="h-4 w-4" /> List
                    </Button>
                  </div>
                  {/* Any authenticated user may schedule (mirrors backend). */}
                  <Button onClick={handleCreate} size="md">
                    <Plus className="h-4 w-4" />
                    Schedule
                  </Button>
                </div>
        }
      />

      {view === 'calendar' ? (
        <MeetingCalendar
          month={month}
          meetings={calendar?.meetings ?? []}
          ceoBusy={calendar?.ceoBusy ?? []}
          ceoName={calendar?.ceo?.name}
          onMonthChange={setMonth}
          onSelectDate={handleSelectDate}
          onOpenMeeting={setDrawerMeeting}
        />
      ) : (
        <>
          {/* Filters */}
          <MeetingsFilterBar
            search={search}
            status={status}
            onSearchChange={setSearch}
            onStatusChange={setStatus}
          />

          {/* Grid */}
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <Spinner />
            </div>
          ) : isError ? (
            <EmptyState
              title="Failed to load meetings"
              description="Check your connection or try refreshing."
            />
          ) : visibleMeetings.length === 0 ? (
            <EmptyState
              title={search ? 'No matching meetings' : 'No meetings scheduled'}
              description={search ? 'Try a different search term.' : 'Schedule your first meeting to get started.'}
              action={!search ? <Button onClick={handleCreate}>Schedule</Button> : undefined}
            />
          ) : (
            <div className="grid flex-1 auto-rows-min grid-cols-1 gap-4 overflow-y-auto md:grid-cols-2 xl:grid-cols-3">
              {visibleMeetings.map((meeting) => (
                <MeetingCard
                  key={meeting.id}
                  meeting={meeting}
                  canEdit={canModify(meeting)}
                  onOpen={setDrawerMeeting}
                  onEdit={handleEdit}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Schedule (rich create) */}
      <ScheduleMeetingModal
        open={scheduleOpen}
        date={scheduleDate}
        ceo={calendar?.ceo ?? null}
        ceoBusy={calendar?.ceoBusy ?? []}
        loading={scheduleMeeting.isPending}
        onClose={() => setScheduleOpen(false)}
        onSubmit={handleSchedule}
      />

      {/* Edit Modal (title/status) */}
      <MeetingFormModal
        open={formOpen}
        meeting={editMeeting}
        loading={createMeeting.isPending || updateMeeting.isPending}
        onClose={() => {
          setFormOpen(false);
          setEditMeeting(null);
        }}
        onCreate={handleCreateSubmit}
        onUpdate={handleUpdateSubmit}
      />

      {/* Detail Drawer */}
      <MeetingDrawer
        meeting={drawerMeeting}
        canModify={drawerMeeting ? canModify(drawerMeeting) : false}
        saving={updateMeeting.isPending}
        onClose={() => setDrawerMeeting(null)}
        onSave={handleUpdateSubmit}
        onDelete={(meeting) => {
          setDrawerMeeting(null);
          setDeleteTarget(meeting);
        }}
      />

      {/* Delete Confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Meeting"
        message={`Delete "${deleteTarget?.title}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteMeeting.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
